/**
 * The "Quality Audits" board, read as data.
 *
 * One definition of what a punch is, what an audit is, which of them the report covers —
 * the punches only, since 24 Sep 2026 — and when one is open. Used by the snapshot job that
 * writes the rows AND by the report endpoint that reads them back, so a snapshot taken under
 * an older rule is still read with today's. Pure: no database, no clock, no Monday client.
 *
 * ⚠ **"Quality HCA" is not a board.** It is the only group (`quality_hca`) of the board
 * *Quality Audits*, id 1786804896, in the Operations workspace on unifiber-be.monday.com.
 * The meeting calls the group by name, which reads like a board and is not one.
 *
 * ⚠ **Columns are addressed by id, never by title.** A title is editable by anyone with the
 * board open; a lookup by title breaks silently the day somebody renames one, and a report
 * that silently counts zero is worse than one that fails.
 *
 * ⚠ **Labels are matched character for character.** `CIF - Circet` with its spaces and
 * capitals is the string the board holds. A label that differs by one capital counts zero,
 * silently — which is also why nothing here normalises, trims or lower-cases a label before
 * storing it.
 *
 * ⚠ **Monday's own `done_colors` are ignored.** On this board they flag `NOK` and
 * `CIF - Circet` as "done". Open and closed are defined here, explicitly.
 */
// ─────────────────────────────────────────────────────────────────────────────
// The board
// ─────────────────────────────────────────────────────────────────────────────
/** Board *Quality Audits*, workspace Operations. Measured 17 Sep 2026: 5 880 items. */
export const QUALITY_AUDITS_BOARD_ID = "1786804896";
/** The group the meeting calls "Quality HCA". Carried for recognition, not for filtering. */
export const QUALITY_HCA_GROUP_ID = "quality_hca";
/**
 * Every column the snapshot reads, by id.
 *
 * Measured on the board 17 Sep 2026. Adding one here is all that is needed to carry it
 * into the snapshot: the read, the raw record and the tests all enumerate this object.
 */
export const COLUMNS = {
    priority: "dropdown_mky9mycm",
    statusUnifiber: "status_mkmm9ce",
    statusContractor: "status_mkmmjfn",
    /** The partner — "Build contractor" on the board. What row scoping matches on. */
    buildContractor: "color_mkp6fe43",
    /** The geographic dimension that matters. POP is secondary and often blank. */
    area: "dropdown_mky0st0r",
    laxonName: "text_mkmf3m43",
    popName: "text_mm0madk",
    /**
     * ⚠ THE ADDRESS. The item's own NAME is not one: measured 22 Sep 2026, all 684
     * dispatched audits are named with a 32-character hex identifier from the system that
     * feeds them, and the punches carry a work reference (`CHAR007`, `10081381`). The street
     * lives in these two columns instead.
     *
     * Coverage, same measurement: `STREET_NM` on 684/684 audits and 901/927 punches;
     * `HNR_FULL` on 502 and 848. So the street is almost always known and the house number
     * often is not — which is why `addressOf` composes what it has rather than assuming both.
     */
    streetName: "text_mkmfqer8",
    houseNumber: "text_mkmf2dcf",
    dispatchDate: "date_1_mkmmx0dd",
    doneDate: "date_2_mkmmc142",
    validatedDate: "date_3_mkmm8w6t",
    /** Only a VLS/FO observation carries this. It is what tells a punch from an audit. */
    typeVlsFo: "dropdown_mkxfd49d",
    vlsFoStatus: "color_mkxfv9w9",
    finaleOkForHc: "color_mkpt79vm",
};
/** What the Monday client asks for. Order is irrelevant; completeness is not. */
export const SNAPSHOT_COLUMN_IDS = Object.values(COLUMNS);
// ─────────────────────────────────────────────────────────────────────────────
// Labels, exactly as the board spells them
// ─────────────────────────────────────────────────────────────────────────────
/** Unifiber's own side of the workflow. */
export const STATUS_UNIFIBER = {
    DISPATCHED: "Dispatched",
    REDO: "Redo",
    VALIDATION_PLANNED: "Validation planned",
    VALIDATED: "Validated",
};
/** The contractor's side. `Done` is the contractor's claim, not a closure — see `isOpen`. */
export const STATUS_CONTRACTOR = {
    ACCEPTED: "Accepted",
    PLANNED: "Planned",
    REFUSED: "Refused",
    DONE: "Done",
};
export const PRIORITIES = ["P1", "P2", "P3"];
export function isPriority(value) {
    return value !== null && PRIORITIES.includes(value);
}
/**
 * One column's text, or null when it is unset.
 *
 * ⚠ Monday returns `""` for an unset column, not null. Collapsing the two here means every
 * caller tests one thing, and stops `""` being counted as a partner, a commune or a
 * priority in its own right.
 */
export function textOf(item, columnId) {
    const value = item.column_values.find((c) => c.id === columnId)?.text;
    return value === null || value === undefined || value === "" ? null : value;
}
/**
 * A board date column's text as `YYYY-MM-DD`, or null.
 *
 * Monday renders a date column as `2026-09-17`, and as `2026-09-17 14:30` when the column
 * carries a time. Only the day is kept: every deadline in this product is counted in whole
 * days from the dispatch date, and a time would invite an age that depends on the hour.
 * Anything that is not a plausible date is null rather than a guess.
 */
export function parseBoardDate(text) {
    if (text === null)
        return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text.trim());
    if (!match)
        return null;
    const [, year, month, day] = match;
    const monthNum = Number(month);
    const dayNum = Number(day);
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31)
        return null;
    return `${year}-${month}-${day}`;
}
/**
 * Punch or audit.
 *
 * A VLS/FO observation is the only kind that carries `Type VLS/FO`; a priority is accepted
 * as evidence too, so an observation whose type was left blank is still counted as the
 * punch it is rather than filed among 4 959 audits.
 */
export function kindOf(item) {
    const isPunch = textOf(item, COLUMNS.typeVlsFo) !== null || textOf(item, COLUMNS.priority) !== null;
    return isPunch ? "PUNCH" : "AUDIT";
}
/**
 * Is this item still open?
 *
 * ⚠ **Only `Validated` closes an item** (Jérémy, 17 Sep 2026). An item the contractor
 * marked `Done` is a claim awaiting Réginald's approval, and counting it as closed would
 * let a contractor close their own punches. An item with no `Status Unifiber` at all is
 * open: nothing has been decided about it yet.
 */
export function isOpen(statusUnifiber) {
    return statusUnifiber !== STATUS_UNIFIBER.VALIDATED;
}
/**
 * Is this row in the report's scope?
 *
 * ⚠ **The VLS/FO observations only** — decided 24 Sep 2026, when the field-quality team
 * wrote that the tickets they need are "exclusively those created in the VLS/FO Observation
 * tab". It replaces the scope of 17 Sep, which also carried the HCA audits that had entered
 * the dispatch workflow — 684 of them on 25 Sep, every one created by the integration. They
 * leave the report, and the snapshot stops storing them.
 *
 * ⚠ **By content, not by creator.** The team's Monday view selects items by who CREATED
 * them — four named people plus a dynamic "Me", which resolves to whoever is looking and so
 * shows Philippe one row more than everybody else. Copied here, that list would have to be
 * kept up to date — a new auditor's punches would vanish until somebody noticed — and "Me"
 * means nothing to an API token. What makes an item an observation is what it carries: a
 * Type VLS/FO or a priority, which is `kindOf`. Measured 25 Sep 2026: 928 by content against
 * 926 in the view, and the difference is seven named items (see CLAUDE.md).
 *
 * ⚠ **No status is filtered here.** The team's view also hides the `Validated` items; the
 * report keeps them, and the reader filters on the status on screen (Jérémy, 25 Sep 2026).
 *
 * ⚠ **It reads the STORED kind.** That is what lets the snapshots of 22–25 Sep — taken under
 * the old scope, audits included — be read with today's rule instead of being rewritten. It
 * is typed on the one field it reads so the API can hand it a database row, whose `kind` is a
 * plain string, and it matches character for character like every other label here.
 */
export function inScope(row) {
    return row.kind === "PUNCH";
}
/** One item → one row. Nothing is normalised, trimmed or re-cased on the way through. */
export function toRow(item) {
    const raw = {};
    for (const columnId of SNAPSHOT_COLUMN_IDS)
        raw[columnId] = textOf(item, columnId);
    const statusUnifiber = textOf(item, COLUMNS.statusUnifiber);
    return {
        mondayItemId: item.id,
        name: item.name,
        groupTitle: item.group?.title ?? null,
        createdAt: item.created_at ?? null,
        kind: kindOf(item),
        priority: textOf(item, COLUMNS.priority),
        statusUnifiber,
        statusContractor: textOf(item, COLUMNS.statusContractor),
        buildContractor: textOf(item, COLUMNS.buildContractor),
        area: textOf(item, COLUMNS.area),
        laxonName: textOf(item, COLUMNS.laxonName),
        popName: textOf(item, COLUMNS.popName),
        streetName: textOf(item, COLUMNS.streetName),
        houseNumber: textOf(item, COLUMNS.houseNumber),
        dispatchDate: parseBoardDate(textOf(item, COLUMNS.dispatchDate)),
        doneDate: parseBoardDate(textOf(item, COLUMNS.doneDate)),
        validatedDate: parseBoardDate(textOf(item, COLUMNS.validatedDate)),
        isOpen: isOpen(statusUnifiber),
        raw,
    };
}
/** The whole board → the rows the snapshot stores. Order is the board's. */
export function scopedRows(items) {
    return items.map(toRow).filter(inScope);
}
// ─────────────────────────────────────────────────────────────────────────────
// Type VLS/FO — a multi-select column
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The labels of a multi-select dropdown, from the text Monday renders for it.
 *
 * ⚠ **Several labels, in the order they were SELECTED.** Monday joins them with ", ", and
 * the board holds both `FACADE, OTHER` and `OTHER, FACADE`. The order is kept as the board
 * has it — so `join(", ")` gives back its exact text — and nothing is trimmed, re-cased or
 * sorted. Deciding that the two orders are the same observation is the counting's job.
 *
 * ⚠ This rests on no label containing ", ". Measured 25 Sep 2026: none of the 13 labels of
 * Type VLS/FO does. One that did would be split in two, which shows up as two unfamiliar
 * bars on the screen rather than as a silent miscount.
 */
export function parseDropdownLabels(text) {
    if (text === null || text === "")
        return [];
    return text.split(", ");
}
/**
 * A row's Type VLS/FO labels, read from what the snapshot stored.
 *
 * ⚠ **From `raw`, not from a typed column.** The column has been read since the first
 * snapshot (22 Sep 2026) and sits in `raw` on every punch of every snapshot — 100 %,
 * measured 25 Sep. A new typed column would be empty on the snapshots already taken, and
 * those cannot be backfilled: the append-only trigger refuses the UPDATE, as it should.
 * Reading `raw` gives every snapshot, old and new, the same answer by the same path — which
 * the weekly comparison will need when it compares a morning with the one a week before.
 *
 * Tolerant on purpose: a `raw` that is missing, not an object, or holds something other than
 * text there reads as "no type", rather than failing the report for every viewer.
 */
export function typesVlsFoOf(raw) {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw))
        return [];
    const value = raw[COLUMNS.typeVlsFo];
    return typeof value === "string" ? parseDropdownLabels(value) : [];
}
/**
 * The street address, as a person would read it.
 *
 * ⚠ **Not the item's name.** The board names an audit with a 32-character hex identifier
 * and a punch with a work reference; neither is an address. Composed from what is actually
 * there: `Rue du Roi Albert 11` when both columns are filled, `Rue du Roi Albert` when only
 * the street is, and `null` when neither is — never a half-built string like `, Dour` or a
 * bare house number, which reads as a bug rather than as missing data.
 *
 * The locality is deliberately NOT appended here: the screen already shows the commune in
 * its own column, and repeating it in every address wastes the width a street name needs.
 */
export function addressOf(row) {
    if (row.streetName === null)
        return null;
    return row.houseNumber === null ? row.streetName : `${row.streetName} ${row.houseNumber}`;
}
//# sourceMappingURL=quality-audits.js.map